namespace $ {

	const tag_regexp = /#\w*/g

	type Field_type = ReturnType< $shm_hitalama_map_widget_entity_form_custom['types_supported'] >[ number ]

	export class $shm_hitalama_map_widget_entity_form_custom extends $giper_baza_entity.with({
		Use_for_fields: $giper_baza_atom_link.to( ()=> $shm_hitalama_map_widget ),
		Object_current: $giper_baza_atom_dict,
		Objects: $giper_baza_atom_list,
		Hide_buttons: $giper_baza_atom_bool, //submit/clear
	}) {

		@ $mol_mem
		hide_buttons( next?: boolean ) {
			return this.Hide_buttons(next)?.val(next) ?? false
		}

		@ $mol_mem
		object_current( next?: Record< string, any > ): Record< string, any > {
			return this.Object_current(next)?.val(next) ?? {}
		}

		@ $mol_mem_key
		object_current_value( name: string, next?: any ) {
			const object = this.object_current()
			if( next === undefined ) return object[ name ] ?? ''

			this.object_current({ ...object, [ name ]: next })
			return next
		}

		@ $mol_mem
		objects( next?: readonly any[] ) {
			return this.Objects(next)?.val(next) ?? []
		}

		@ $mol_mem
		widget_with_fields() {
			return this.Use_for_fields()?.remote()
		}
		
		@ $mol_mem
		text( next?: string ): string {
			const widget = this.widget_with_fields()
			if( next !== undefined ) {
				if( !widget || widget.Data_method()?.val() ) return this.text()
				if( widget.data() !== undefined ) widget.data( next )
				else widget.text( next )
				return next
			}
			return widget?.data() ?? widget?.text() ?? ''
		}
		
		@ $mol_mem
		fields_parsed() {
			const lines = this.text().split('\n').flatMap( line => line.split('|') ).map( s => s.trim() ).filter( Boolean )

			const dict: Record< string, ReturnType< $shm_hitalama_map_widget_entity_form_custom['field_parse'] > > = {}

			const arr = lines.map( line => {
				const parsed = this.field_parse( line )
				dict[ parsed.name ] = parsed
				return parsed
			} )
			
			return { dict, arr }
		}
		
		@ $mol_action
		field_parse( str: string ) {
			const tags: string[] = []

			const name_with_required = str.replaceAll( tag_regexp, tag => {
				tags.push( tag )
				return ''
			} ).trim()
			const required = /\*\s*$/.test( name_with_required )
			const name = name_with_required.replace( /\*\s*$/, '' ).trim()
			
			const type_tag = tags.find( t => this.types_supported().includes( t.slice(1) as Field_type ) )

			return {
				tags,
				name,
				required,
				type: (type_tag?.slice(1) ?? 'any') as Field_type,
			}
		}

		@ $mol_mem
		field_names() {
			return this.fields_parsed().arr.map( f => f.name )
		}

		@ $mol_mem
		input_field_names() {
			return this.fields_parsed().arr.filter( field => field.type !== 'custom' ).map( field => field.name )
		}

		types_supported() {
			return [
				'any',
				'textarea',
				'string',
				'number',
				'date',
				'period',
				'file',
				'file_pb',
				'atom_link',
				'list_link',
				'select',
				'json',
				'custom',
			] as const
		}

		field_link_options( name: string ): Record< string, string > {
			return {}
		}

		field_select_options( name: string ): readonly string[] {
			return []
		}

		field_format( field: ReturnType< $shm_hitalama_map_widget_entity_form_custom['field_parse'] > ) {
			return `${ field.name }${ field.required ? '*' : '' }${ field.tags.length ? ` ${ field.tags.join( ' ' ) }` : '' }`
		}

		fields_mutable() {
			const owner = this.widget_with_fields()
			return Boolean( owner && !owner.Data_method()?.val() )
		}

		@ $mol_action
		fields_write( fields: readonly ReturnType< $shm_hitalama_map_widget_entity_form_custom['field_parse'] >[] ) {
			const owner = this.widget_with_fields()
			if( !owner || owner.Data_method()?.val() ) return false

			const text = fields.map( field => this.field_format( field ) ).join( '\n' )
			this.text( text )
			return true
		}

		@ $mol_action
		field_add( name: string, type: Field_type = 'any' ) {
			if( this.field_names().includes( name ) ) return false
			const tags = type === 'any' ? [] : [ `#${ type }` ]
			return this.fields_write([ ...this.fields_parsed().arr, { name, required: false, tags, type } ])
		}

		@ $mol_action
		field_type_set( name: string, type: Field_type ) {
			if( !this.fields_mutable() ) return false
			const supported = this.types_supported()
			let found = false
			const fields = this.fields_parsed().arr.map( field => {
				if( field.name !== name ) return field
				found = true
				const tags = field.tags.filter( tag => !supported.includes( tag.slice( 1 ) as Field_type ) )
				if( type !== 'any' ) tags.push( `#${ type }` )
				return { ...field, tags, type }
			} )
			if( !found ) return false
			this.field_values_type_prepare( name, type )
			return this.fields_write( fields )
		}

		field_link_value_prepare( value: any, type: 'atom_link' | 'list_link' ) {
			const values = Array.isArray( value ) ? value : [ value ]
			const links = [ ... new Set( values.flatMap( value => {
				const source = value instanceof $giper_baza_link ? value.str : String( value ?? '' ).trim()
				if( !source || $giper_baza_link.check( source ) === null ) return []
				return [ new $giper_baza_link( source ).str ]
			}) ) ]
			return type === 'list_link' ? links : links.length === 1 ? links[ 0 ] : ''
		}

		@ $mol_action
		field_values_type_prepare( name: string, type: Field_type ) {
			if( type !== 'atom_link' && type !== 'list_link' ) return
			const objects = this.objects() as readonly Record< string, any >[]
			this.objects( objects.map( object => ({
				... object,
				[ name ]: this.field_link_value_prepare( object?.[ name ], type ),
			}) ) )
			const current = this.object_current() as Record< string, any >
			this.object_current({
				... current,
				[ name ]: this.field_link_value_prepare( current?.[ name ], type ),
			})
		}

		@ $mol_action
		field_remove( name: string ) {
			const fields = this.fields_parsed().arr.filter( field => field.name !== name )
			if( fields.length === this.fields_parsed().arr.length ) return false
			return this.fields_write( fields )
		}

		@ $mol_mem_key
		field_type( name: string ) {
			return this.fields_parsed().dict?.[name]?.type
		}

		@ $mol_mem_key
		field_required( name: string ) {
			return this.fields_parsed().dict?.[ name ]?.required ?? false
		}

		@ $mol_mem_key
		field_tags( name: any ) {
			return this.fields_parsed().dict?.[name]?.tags ?? []
		}

		@ $mol_action
		obj_add( obj: any ) {
			this.objects( [ ...this.objects(), obj ] )
		}
		
	}

}
